import React, { useMemo, useState } from "react";
import {
  LayoutDashboard,
  Users,
  ArrowLeftRight,
  ShieldAlert,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ScrollText
} from "lucide-react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAuthMutations } from "../hooks/useAuthMutations";
import { usePermissions, PERMISSIONS } from "../hooks/usePermissions";
import { useAdminAttention } from "../hooks/useAdmin";

/**
 * Navigation is filtered by permission so staff only see the sections they can
 * actually use. This is presentation only - each backend route enforces its own
 * permission independently, so hiding an entry is never the security control.
 */
/** Which nav entry each attention bucket contributes its count to. */
const BUCKET_NAV_PATHS = {
  transfers_stuck: "/admin/transfers",
  transfers_refunding: "/admin/transfers",
  transfers_manual_review: "/admin/transfers",
  transfers_compliance_review: "/admin/transfers",
  compliance_cases_open: "/admin/compliance",
  kyc_pending_too_long: "/admin/users",
};

const ADMIN_NAV = [
  {
    label: "Overview",
    path: "/admin",
    icon: LayoutDashboard,
    permission: PERMISSIONS.METRICS_READ,
  },
  {
    label: "Users & KYC",
    path: "/admin/users",
    icon: Users,
    permission: PERMISSIONS.USERS_READ,
  },
  {
    label: "Transfers",
    path: "/admin/transfers",
    icon: ArrowLeftRight,
    permission: PERMISSIONS.TRANSFERS_READ,
  },
  {
    label: "Compliance & Risk",
    path: "/admin/compliance",
    icon: ShieldAlert,
    permission: PERMISSIONS.COMPLIANCE_READ,
  },
  {
    label: "Audit Log",
    path: "/admin/audit",
    icon: ScrollText,
    permission: PERMISSIONS.AUDIT_READ,
  },
  {
    label: "Settings",
    path: "/admin/settings",
    icon: Settings,
    permission: PERMISSIONS.ROUTING_READ,
  },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { logout, isLoggingOut } = useAuthMutations();
  const { can, isLoadingPermissions } = usePermissions();

  // Counts of outstanding work, so an operator sees where the queue is without
  // opening each page. Derived on read by the backend - there is no scheduler,
  // so this is a queue indicator, not alerting.
  const { buckets } = useAdminAttention();
  const navBadges = useMemo(() => {
    const totals = {};
    for (const bucket of buckets) {
      const path = BUCKET_NAV_PATHS[bucket.key];
      if (path) totals[path] = (totals[path] ?? 0) + bucket.count;
    }
    return totals;
  }, [buckets]);

  // While permissions load, show nothing rather than flashing the full menu
  // and then removing entries the user cannot access.
  const visibleNav = isLoadingPermissions
    ? []
    : ADMIN_NAV.filter((item) => !item.permission || can(item.permission));

  const signOut = () => {
    logout(undefined, { onSuccess: () => navigate("/login") });
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex flex-col md:flex-row text-[#0A2540]">
      
      {/* MOBILE HEADER & NAV */}
      <div className="md:hidden bg-[#0A4730] px-6 pt-6 pb-5 flex items-center justify-between text-white sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center font-serif font-bold text-[#C0F6B7]">
            {user?.full_name?.charAt(0)?.toUpperCase() || "A"}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Admin Portal</p>
            <h1 className="text-lg text-[#C0F6B7] font-serif font-semibold leading-tight">
              Dashboard
            </h1>
          </div>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
        >
          {mobileMenuOpen ? <X size={20} className="text-white" /> : <Menu size={20} className="text-white" />}
        </button>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-x-0 top-[81px] bg-[#0A4730] border-t border-white/10 p-6 z-40 shadow-2xl animate-in slide-in-from-top duration-200">
          <div className="space-y-2">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;

              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl transition ${
                    active
                      ? "bg-white/15 text-[#C0F6B7] font-semibold"
                      : "text-white/80 hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} />
                    <span className="text-sm">{item.label}</span>
                  </div>

                  <ChevronRight size={16} className="opacity-50" />
                </button>
              );
            })}
          </div>

          {/* Mobile Sign Out */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                signOut();
              }}
              disabled={isLoggingOut}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-medium text-red-300 hover:bg-white/5 transition disabled:opacity-50"
            >
              <LogOut size={18} />

              <span>
                {isLoggingOut ? "Signing out…" : "Sign Out"}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-100 p-6 sticky top-0 h-screen">
        {/* The avatar was decorative; it is now the way into your own record,
            which is where "what am I allowed to do?" is answered. */}
        <button
          type="button"
          onClick={() => navigate("/admin/profile")}
          aria-label="Your staff profile"
          className="flex items-center gap-3 px-2 mb-8 text-left rounded-2xl hover:opacity-80 transition"
        >
          <div className="w-10 h-10 rounded-2xl bg-[#0A4730] flex items-center justify-center font-serif font-bold text-[#C0F6B7] text-lg shadow-sm">
            {user?.full_name?.charAt(0)?.toUpperCase() || "A"}
          </div>
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40">Control Center</p>
            <h1 className="text-base text-[#0A4730] font-serif font-semibold">
              Admin Portal
            </h1>
          </div>
        </button>

        <nav className="flex-1 space-y-1.5">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition duration-200 ${
                  active 
                    ? "bg-[#0A4730] text-white shadow-sm" 
                    : "text-[#0A2540]/70 hover:bg-[#F5F9F4] hover:text-[#0A4730]"
                }`}
              >
                <Icon size={18} className={active ? "text-[#C0F6B7]" : "text-[#0A4730]/60"} />
                <span className="flex-1 text-left">{item.label}</span>
                {navBadges[item.path] > 0 && (
                  <span
                    aria-label={`${navBadges[item.path]} items need attention`}
                    className={`min-w-5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums ${
                      active ? "bg-[#C0F6B7] text-[#0A4730]" : "bg-red-50 text-red-600"
                    }`}
                  >
                    {navBadges[item.path]}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="pt-4 border-t border-slate-100">
          <button 
            onClick={signOut}
            disabled={isLoggingOut}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-red-500 hover:bg-red-50 transition"
          >
            <LogOut size={18} />
            <span>{isLoggingOut ? "Signing out…" : "Sign Out"}</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA  */}
      <main className="flex-1 p-6 md:p-10 max-w-7xl mx-auto w-full">
        <Outlet />
      </main>

    </div>
  );
}
